import { useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { Card, ErrorBanner, Notice, PageHeader, Spinner, StatusBadge, fmt, useLoad } from "../../components/kit";
import { Progress } from "../../components/ui/progress";
import { label } from "../../components/Ai";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { AXIS_TICK, BAR_RADIUS, CURSOR, INITIAL_DIM, TOOLTIP_STYLE } from "../../lib/chart";

const METRICS = ["sent", "accepted", "delivered", "bounced", "complained", "failed"];

export function SendingStatus({ sending }) {
  const { t } = useT();
  if (!sending) return null;
  if (!sending.configured) return <Notice tone="error" title={t("notconfigured")} testId="sending-not-configured">Sending and test-send actions are disabled until a platform administrator configures the SMTP provider.</Notice>;
  if (!sending.can_send) return <Notice tone="warn" title="Sending is blocked" testId="sending-blocked">{sending.blockers.join(" · ")}</Notice>;
  return <Notice tone="ok" title={t("dash.ready")} testId="sending-ready">{t("Messages are relayed through")} {sending.provider}.</Notice>;
}

function Metric({ k, value, reason }) {
  const { t } = useT();
  const unavailable = value === null || value === undefined;
  return (
    <div className="bg-[#0B0A0F] p-5" data-testid={`dashboard-metric-${k}`}>
      <div className="text-xs font-medium uppercase tracking-wider text-zinc-500">{t(`m.${k}`)}</div>
      {unavailable ? (
        <div className="mt-3"><div className="text-lg font-semibold text-zinc-500" data-testid={`dashboard-metric-${k}-unavailable`}>{t("common.unavailable")}</div>
          <div className="mt-1 text-[11px] text-zinc-600">{reason}</div></div>
      ) : <div className="mt-3 font-display text-3xl font-semibold text-white" data-testid={`dashboard-metric-${k}-value`}>{value.toLocaleString()}</div>}
    </div>
  );
}

function UsageBar({ label, used, limit, testId }) {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="flex justify-between text-xs"><span className="text-zinc-400">{label}</span><span className="font-mono text-zinc-300">{used.toLocaleString()} / {limit == null ? "Unlimited" : limit.toLocaleString()}</span></div>
      <Progress value={pct} className="h-1.5 bg-white/5" />
    </div>
  );
}

export { UsageBar };

function BounceReasons({ rows }) {
  const { t } = useT();
  const total = rows.reduce((a, r) => a + r.count, 0);
  return (
    <Card testId="dashboard-bounce-reasons">
      <div className="mb-1 text-sm font-medium text-white">{t("Why emails bounced")}</div>
      <p className="mb-4 text-xs text-zinc-500">{t("Bounces and rejections in this range, grouped by reason. Open a bounced message for a plain-language explanation.")}</p>
      <div className="space-y-2.5">{rows.map((r) => (
        <div key={r.reason} data-testid={`bounce-reason-${r.reason}`}>
          <div className="mb-1 flex justify-between text-xs"><span className="text-zinc-300">{label(r.reason)}</span><span className="font-mono text-zinc-400">{r.count}</span></div>
          <div className="h-1.5 overflow-hidden rounded-full bg-white/5"><div className="h-full rounded-full bg-purple-500" style={{ width: `${Math.round((r.count / total) * 100)}%` }} /></div>
        </div>))}</div>
    </Card>
  );
}

function TagAnalytics({ range }) {
  const { t } = useT();
  const { data, error } = useLoad(() => api.get("/app/analytics/tags", { params: { range } }).then((r) => r.data), [range]);
  const cell = (v, suffix = "") => (v === null || v === undefined ? <span className="text-zinc-600">n/a</span> : `${v.toLocaleString()}${suffix}`);
  return (
    <Card testId="tag-analytics">
      <div className="mb-4 flex items-center justify-between"><span className="text-sm font-medium text-white">{t("Results by tag")}</span>
        <span className="hidden text-xs text-zinc-500 sm:inline">{t("Tag emails with e.g.")} {"{"}"name":"type","value":"receipt"{"}"}</span></div>
      <ErrorBanner error={error} />
      {data && (data.rows.length === 0 ? <p className="text-sm text-zinc-500" data-testid="tag-analytics-empty">{t("No live messages in this range yet.")}</p> : (
        <div className="overflow-x-auto"><table className="w-full min-w-[640px] text-left text-sm">
          <thead className="text-xs uppercase tracking-wider text-zinc-500"><tr><th className="py-2">{t("Tag")}</th><th>{t("m.sent")}</th><th>{t("m.accepted")}</th><th>{t("m.delivered")}</th><th>{t("m.bounced")}</th><th>{t("m.complained")}</th><th>{t("m.failed")}</th><th>{t("Delivery rate")}</th></tr></thead>
          <tbody>{data.rows.map((r) => <tr key={r.tag} className="border-t border-white/5" data-testid={`tag-row-${r.tag}`}>
            <td className="py-2.5 font-mono text-xs text-purple-200">{r.tag}</td><td>{cell(r.sent)}</td><td>{cell(r.accepted)}</td><td>{cell(r.delivered)}</td>
            <td>{cell(r.bounced)}</td><td>{cell(r.complained)}</td><td>{cell(r.failed)}</td><td>{cell(r.delivery_rate, "%")}</td></tr>)}</tbody>
        </table></div>))}
    </Card>
  );
}

const RANGES = ["24h", "7d", "30d"];

function RangePicker({ range, setRange }) {
  return (
    <div className="inline-flex rounded-lg border border-white/10 p-0.5" role="group" aria-label="Time range">
      {RANGES.map((r) => (
        <button key={r} onClick={() => setRange(r)} aria-pressed={range === r} data-testid={`range-${r}`}
          className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${range === r ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white"}`}>{r}</button>
      ))}
    </div>
  );
}

function ReputationAlert({ a }) {
  const critical = a.level === "critical";
  const title = `${critical ? "Critical" : "Warning"}: ${a.metric === "bounced" ? "bounce" : "complaint"} rate ${a.rate}%`;
  return <Notice tone={critical ? "error" : "warn"} title={title} testId={`reputation-alert-${a.metric}`}>{a.message}</Notice>;
}

function SubmittedChart({ series }) {
  const { t } = useT();
  return (
    <Card className="lg:col-span-2" testId="dashboard-chart">
      <div className="mb-4 text-sm font-medium text-white">{t("Submitted messages")}</div>
      {series.length > 0 && <div className="h-48"><ResponsiveContainer initialDimension={INITIAL_DIM}><BarChart data={series}>
        <XAxis dataKey="bucket" tick={AXIS_TICK} axisLine={false} tickLine={false} />
        <Tooltip cursor={CURSOR} contentStyle={TOOLTIP_STYLE} />
        <Bar dataKey="sent" fill="#9333EA" radius={BAR_RADIUS} />
      </BarChart></ResponsiveContainer></div>}
      {!series.length && <p className="py-12 text-sm text-zinc-500" data-testid="chart-empty">{t("No live messages in this range yet.")}</p>}
    </Card>
  );
}

function UsageCard({ usage, domains }) {
  const { t } = useT();
  return (
    <Card testId="dashboard-usage">
      <div className="mb-4 text-sm font-medium text-white">{t("Usage limits")}</div>
      <div className="space-y-4">
        <UsageBar label={t("Today")} used={usage.today} limit={usage.limits.daily} testId="usage-today" />
        <UsageBar label={t("This month")} used={usage.month} limit={usage.limits.monthly} testId="usage-month" />
        <div className="text-xs text-zinc-500">{t("Rate limit")}: {usage.limits.unlimited ? t("unlimited (super admin)") : `${usage.limits.per_minute} / ${t("minute")}`}</div>
      </div>
      <div className="mt-6 border-t border-white/5 pt-4 text-sm">
        <div className="mb-2 font-medium text-white">{t("nav.domains")}</div>
        {domains.map((d) => (
          <div key={d.id} className="flex items-center justify-between py-1"><span className="truncate font-mono text-xs text-zinc-300">{d.name}</span><StatusBadge status={d.status} /></div>))}
        {!domains.length && <Link to="/app/domains" className="text-xs text-purple-300">{t("Add a sending domain \u2192")}</Link>}
      </div>
    </Card>
  );
}

function RecentMessages({ rows }) {
  const { t } = useT();
  return (
    <Card testId="dashboard-recent" className="min-w-0">
      <div className="mb-4 flex justify-between text-sm"><span className="font-medium text-white">{t("Recent messages")}</span><Link to="/app/messages" className="text-xs text-purple-300">{t("All \u2192")}</Link></div>
      {rows.map((m) => (
        <div key={m.id} className="flex items-center justify-between gap-3 border-t border-white/5 py-2.5 text-sm first:border-0">
          <div className="min-w-0"><div className="truncate text-zinc-200">{m.subject}</div><div className="text-xs text-zinc-500">{fmt(m.created_at)}</div></div>
          <StatusBadge status={m.status} />
        </div>))}
      {!rows.length && <p className="text-sm text-zinc-500">{t("No messages yet.")}</p>}
    </Card>
  );
}

function RecentActivity({ rows }) {
  const { t } = useT();
  return (
    <Card testId="dashboard-activity" className="min-w-0">
      <div className="mb-4 text-sm font-medium text-white">{t("Recent activity")}</div>
      {rows.map((a) => (
        <div key={a.id} className="flex justify-between gap-3 border-t border-white/5 py-2.5 text-sm first:border-0">
          <span className="font-mono text-xs text-purple-200">{a.action}</span><span className="text-xs text-zinc-500">{fmt(a.created_at)}</span>
        </div>))}
      {!rows.length && <p className="text-sm text-zinc-500">{t("No activity yet.")}</p>}
    </Card>
  );
}

function CampaignsCard() {
  const { t } = useT();
  const { data } = useLoad(() => api.get("/app/campaigns-summary").then((r) => r.data), []);
  if (!data) return null;
  return (
    <Card testId="dashboard-campaigns" className="min-w-0">
      <div className="mb-4 flex justify-between text-sm"><span className="font-medium text-white">{t("nav.campaigns")}</span>
        <span className="text-xs text-zinc-500" data-testid="dashboard-contacts-count">{data.contacts.toLocaleString()} {t("nav.audiences").toLowerCase()} · <Link to="/app/campaigns" className="text-purple-300">{t("All \u2192")}</Link></span></div>
      {data.campaigns.map((c) => (
        <Link key={c.id} to={`/app/campaigns/${c.id}`} className="flex items-center justify-between gap-3 border-t border-white/5 py-2.5 text-sm first:border-0 hover:text-white">
          <div className="min-w-0"><div className="truncate text-zinc-200">{c.name}</div><div className="text-xs text-zinc-500">{(c.progress?.sent || 0).toLocaleString()} / {(c.audience_size || 0).toLocaleString()} · {fmt(c.finished_at || c.scheduled_at || c.created_at)}</div></div>
          <StatusBadge status={c.status} />
        </Link>))}
      {!data.campaigns.length && <Link to="/app/campaigns" className="text-sm text-purple-300" data-testid="dashboard-campaigns-empty">{t("Start your first campaign \u2192")}</Link>}
    </Card>
  );
}

function DashboardBody({ data, range }) {
  const { t } = useT();
  return (
    <div className="space-y-6">
      <SendingStatus sending={data.sending} />
      {data.alerts?.map((a) => <ReputationAlert key={a.metric} a={a} />)}
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10 md:grid-cols-3 lg:grid-cols-6 rise">
        {METRICS.map((k) => <Metric key={k} k={k} value={data.metrics[k]} reason={data.unavailable_reason} />)}
      </div>
      <p className="text-xs text-zinc-500">{t("\"Accepted\" means the upstream SMTP server accepted the message \u2014 not confirmed inbox delivery.")} {data.test_messages > 0 && `${data.test_messages} ${t("test-mode message(s) excluded.")}`}</p>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <SubmittedChart series={data.series} />
        <UsageCard usage={data.usage} domains={data.domains} />
      </div>
      {data.bounce_reasons?.length > 0 && <BounceReasons rows={data.bounce_reasons} />}
      <TagAnalytics range={range} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <CampaignsCard />
        <RecentMessages rows={data.recent_messages} />
        <RecentActivity rows={data.activity} />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useT();
  const [range, setRange] = useState("7d");
  const { data, error, loading } = useLoad(() => api.get("/app/dashboard", { params: { range } }).then((r) => r.data), [range]);
  return (
    <div>
      <PageHeader title={t("dash.title")} description={t("dash.desc")} actions={<RangePicker range={range} setRange={setRange} />} />
      <ErrorBanner error={error} />
      {loading && !data && <Spinner />}
      {data && <DashboardBody data={data} range={range} />}
    </div>
  );
}
